import {priority} from '@priority-software/priority-proto-api/lib';
import {
    ReportParam,
    ChartConfig,
    MultiChartSerie,
    ReportColumn,
    ReportInputMetaData,
    DateProps
} from './types';
import {
    getMultiSeriesCategories,
    getSeriesNames,
    genScaleMarks,
    isTwoInputsOperator
} from './utils';
import {
    gaugeSettings,
    defaultThousandSign,
    defaultDecimalSign,
    coralColor,
    ChartDimensions,
    SingleSeriesChartTypesOptions,
    topXMaxValue
} from './chartConsts';
import {formatNumber} from '../../features/Form/utils/number';
import {SerializedReportParams} from './types';
import {getLang, isRTL} from '../../Localization';
import {parseHours} from '../../features/Form/utils/time';

const serializeOperatorParam = (operator: string): priority.netitems.shared.ComparisonOperator => {
    switch (operator) {
        case '0':
            return priority.netitems.shared.ComparisonOperator.Eq;
        case '1':
            return priority.netitems.shared.ComparisonOperator.Lt;
        case '2':
            return priority.netitems.shared.ComparisonOperator.Le;
        case '3':
            return priority.netitems.shared.ComparisonOperator.Gt;
        case '4':
            return priority.netitems.shared.ComparisonOperator.Ge;
        case '5':
            return priority.netitems.shared.ComparisonOperator.Ne;
        case '6':
            return priority.netitems.shared.ComparisonOperator.Between;
        case '7':
            return priority.netitems.shared.ComparisonOperator.AndNot;
        case '8':
            return priority.netitems.shared.ComparisonOperator.Eq;
        default:
            return null;
    }
};

export const gwtOperators = {
    Eq: '0',
    Lt: '1',
    Le: '2',
    Gt: '3',
    Ge: '4',
    Ne: '5',
    Between: '6',
    AndNot: '7',
    Or: '8'
};

export const serializeReportParams = (reportParams: ReportParam[]): SerializedReportParams[] => {
    return reportParams?.map(p => {
        const Operator = serializeOperatorParam(p.operator);
        const values = isTwoInputsOperator(p.operator) ? [p.value, p.value2] : [p.value];
        return {
            Operator,
            OriginalOp: p.operator,
            OriginalOp2: p.operator2,
            Values: values
        };
    });
};

/* Use to get column index of non numeric fields */
const getColIndex = (columns: priority.netitems.nrest.IDataColumn[], axis: ReportColumn) => {
    const colId = axis.IsCompany ? 1 : Number(axis.ColId);
    return columns.findIndex(col => col.Id === colId);
};

export const getOriginalCompanytitle = (
    label: string,
    companies: priority.netitems.nrest.ICompany[]
) => {
    const company = companies.find(company => company.Translation === label);
    return company ? company.Title : label;
};

const updateChartAttributesByType = (type: SingleSeriesChartTypesOptions, chart) => {
    let updates;
    switch (type) {
        case SingleSeriesChartTypesOptions.pyramid:
            updates = {
                showLegend: 1,
                showValues: 1
            };
            break;
        case SingleSeriesChartTypesOptions.doughnut2d:
        case SingleSeriesChartTypesOptions.pie2d:
            updates = {
                showPercentValues: 0,
                showValuesInLegend: 0,
                showValueAsPercentInLegend: 0
            };
            break;
        default:
            updates = {};
    }
    return {...chart, ...updates};
};

export const genBasicChartData = (config: ChartConfig, dataSource) => {
    const {xAxis, yAxis, topX, data, explanatoryReport, type} = config;
    const maxValues: number = topX ? Number(topX) : topXMaxValue;
    const {Columns, Rows, Companies} = data;
    const xAxisIdIndex = getColIndex(Columns, xAxis);
    const yAxisIdIndex = Columns.findIndex(col => col.Id === Number(yAxis.ColId));
    const numericFormat = yAxis.NumericFormat;
    const hasNumericFormat = numericFormat && Number(numericFormat) !== 0;
    const dataRows = Rows.map((row, idx) => {
        const rowValues = row.Val.Val;
        const formattedValue = rowValues[yAxisIdIndex];
        const label = rowValues[xAxisIdIndex];
        const value = hasNumericFormat ? row.Orig.Val[yAxisIdIndex] : formattedValue;
        const isPieOrDoughnut =
            type === SingleSeriesChartTypesOptions.pie2d ||
            type === SingleSeriesChartTypesOptions.doughnut2d;
        const tooltext = isPieOrDoughnut
            ? '$label,{br} $percentValue'
            : hasNumericFormat
            ? `$label,{br} ${formattedValue}`
            : null;

        return {
            label,
            value,
            origIdx: idx,
            tooltext,
            id: xAxis.IsCompany
                ? getOriginalCompanytitle(label, Companies)
                : row.Orig.Val[xAxisIdIndex],
            link: explanatoryReport ? 'javascript:void()' : null
        };
    });

    const chartData = dataRows
        .sort((a, b) => Number(b.value) - Number(a.value))
        .slice(0, maxValues)
        .sort((a, b) => a.origIdx - b.origIdx);
    dataSource.data = chartData;

    dataSource.chart = updateChartAttributesByType(
        type as SingleSeriesChartTypesOptions,
        dataSource.chart
    );

    return dataSource;
};

export const getFunnelChartData = (config: ChartConfig, dataSource) => {
    const {xAxis, yAxis, topX, data, explanatoryReport} = config;
    const maxValues: number = topX ? Number(topX) : topXMaxValue;
    const {Columns, Rows, Companies} = data;
    const xAxisIdIndex = getColIndex(Columns, xAxis);
    const yAxisIdIndex = Columns.findIndex(col => col.Id === Number(yAxis.ColId));
    const numericFormat = yAxis.NumericFormat;
    const hasNumericFormat = numericFormat && Number(numericFormat) !== 0;
    const dataRows = Rows.map((row, idx) => {
        const rowValues = row.Val.Val;
        const label = rowValues[xAxisIdIndex];
        const value = hasNumericFormat ? row.Orig.Val[yAxisIdIndex] : rowValues[yAxisIdIndex];
        return {
            label,
            value,
            origIdx: idx,
            id: xAxis.IsCompany
                ? getOriginalCompanytitle(label, Companies)
                : row.Orig.Val[xAxisIdIndex],
            link: explanatoryReport ? 'javascript:void()' : null
        };
    });

    const chartData = dataRows
        .sort((a, b) => Number(b.value) - Number(a.value))
        .slice(0, maxValues)
        .sort((a, b) => a.origIdx - b.origIdx);
    dataSource.data = chartData;

    dataSource.chart = {
        caption: dataSource.chart.caption,
        subCaption: dataSource.chart.subCaption,
        theme: dataSource.chart.theme,
        paletteColors: dataSource.chart.paletteColors,
        streamlinedData: 0,
        showLegend: 1,
        showValues: 1
    };

    return dataSource;
};

export const genBubbleChartData = (config: ChartConfig, dataSource) => {
    const {xAxis, yAxis, data, bubbleConfig, explanatoryReport} = config;
    const {zBubbleSize, category, minX, minY, maxX, maxY} = bubbleConfig;
    const {Columns, Rows, Companies} = data;
    const xAxisIdIndex = Columns.findIndex(col => col.Id === Number(xAxis.ColId));
    const yAxisIdIndex = Columns.findIndex(col => col.Id === Number(yAxis.ColId));
    const zAxisIdIndex = Columns.findIndex(col => col.Id === Number(zBubbleSize.ColId));
    const categoryIndex = getColIndex(Columns, category);
    const xHasNumericFormat = xAxis.NumericFormat && Number(xAxis.NumericFormat) < 0;
    const yHasNumericFormat = yAxis.NumericFormat && Number(yAxis.NumericFormat) < 0;

    const dataInRange = Rows.filter(row => {
        const rowXValues = xHasNumericFormat ? row.Orig.Val : row.Val.Val;
        const rowYValues = yHasNumericFormat ? row.Orig.Val : row.Val.Val;
        return (
            Number(rowXValues[xAxisIdIndex]) >= Number(minX) &&
            Number(rowXValues[xAxisIdIndex]) <= Number(maxX) &&
            Number(rowYValues[yAxisIdIndex]) >= Number(minY) &&
            Number(rowYValues[yAxisIdIndex]) <= Number(maxY)
        );
    });

    const chartData = dataInRange.map(row => {
        const rowValues = row.Val.Val;
        const name = rowValues[categoryIndex];
        return {
            x: rowValues[xAxisIdIndex],
            y: rowValues[yAxisIdIndex],
            z: rowValues[zAxisIdIndex],
            name,
            originalCategoryValue: category.IsCompany
                ? getOriginalCompanytitle(name, Companies)
                : row.Orig.Val[categoryIndex],
            color: coralColor,
            link: explanatoryReport ? 'javascript:void()' : null
        };
    });
    dataSource.chart = {
        ...dataSource.chart,
        showValues: 1,
        xAxisMinValue: minX,
        xAxisMaxValue: maxX,
        yAxisMinValue: minY,
        yAxisMaxValue: maxY,
        plotTooltext: '$name: $xvalue, $yvalue, $zvalue'
    };
    dataSource.dataset = [{data: chartData}];
    return dataSource;
};

export const genScalesChartData = (config: ChartConfig, dataSource) => {
    const {scales, explanatoryReport, yAxis, data} = config;
    const {Columns, Rows} = data;
    const yAxisIdIndex = Columns.findIndex(col => col.Id === Number(yAxis.ColId));
    const numericFormat = yAxis.NumericFormat;
    const hasNumericFormat = numericFormat && Number(numericFormat) !== 0;
    const formattedValue = Rows[0].Val.Val[yAxisIdIndex];
    const value = hasNumericFormat ? Rows[0].Orig.Val[yAxisIdIndex] : formattedValue;

    if (!scales) {
        return null;
    } else {
        const {lowerLimit, upperLimit, colorRange} = genScaleMarks(Array.from(scales.fieldsMap));
        dataSource.chart = {
            ...dataSource.chart,
            upperLimit: upperLimit.toString(),
            lowerLimit: lowerLimit.toString(),
            ...gaugeSettings
        };
        return {
            ...dataSource,
            colorRange,
            dials: {
                dial: [
                    {
                        value,
                        displayValue: hasNumericFormat ? formattedValue : null,
                        link: explanatoryReport ? 'javascript:void()' : null
                    }
                ]
            }
        };
    }
};

export const genKPIChartData = (config: ChartConfig) => {
    const {yAxis, data} = config;
    const {Columns, Rows} = data;
    const yAxisIdIndex = Columns.findIndex(col => col.Id === Number(yAxis.ColId));
    const dataRow = Rows[0];
    const value = dataRow.Val.Val[yAxisIdIndex];
    const numericFormat = yAxis.NumericFormat;
    const hasNumericFormat = numericFormat && Number(numericFormat) !== 0;
    if (hasNumericFormat) {
        return value;
    }
    return formatNumber(value, 0, defaultThousandSign, defaultDecimalSign);
};

export const genMultiSeriesChartData = (config: ChartConfig, dataSource) => {
    const {xAxis, yAxis, topX, data, series, explanatoryReport} = config;
    const maxValues: number = topX ? Number(topX) : topXMaxValue;
    const {Columns, Rows, Companies} = data;
    const xAxisIdIndex = getColIndex(Columns, xAxis);
    const yAxisIdIndex = Columns.findIndex(col => col.Id === Number(yAxis.ColId));
    const numericFormat = yAxis.NumericFormat;
    const hasNumericFormat = numericFormat && Number(numericFormat) !== 0;
    const seriesIndex = getColIndex(Columns, series);
    const categories = [getMultiSeriesCategories(Rows, xAxisIdIndex, xAxis.IsCompany, Companies)];
    const seriesnames: MultiChartSerie[] = getSeriesNames(
        Rows,
        seriesIndex,
        series.IsCompany,
        Companies
    );
    const res = seriesnames.map(serie => {
        const chartCategories = categories[0].category;
        for (let i = 0; i < chartCategories.length; i++) {
            const dataRow = Rows.find(
                row =>
                    row.Val.Val[xAxisIdIndex] === chartCategories[i].label &&
                    row.Val.Val[seriesIndex] === serie.seriesname
            );

            const formattedValue = dataRow ? dataRow.Val.Val[yAxisIdIndex] : '0';

            const value = !dataRow
                ? 0
                : hasNumericFormat
                ? Number(dataRow.Orig.Val[yAxisIdIndex])
                : Number(formattedValue);
            serie.data.push({value, formattedValue});
        }
        return serie;
    });

    const link = explanatoryReport ? 'javascript:void()' : null;
    const dataset = res.map(serie => {
        return {
            ...serie,
            data: serie.data.map(item =>
                item.value
                    ? {
                          value: item.value.toString(),
                          formattedValue: item.formattedValue,
                          tooltext: hasNumericFormat
                              ? `${serie.seriesname}, $label, ${item.formattedValue}`
                              : null,
                          link
                      }
                    : {value: '0'}
            )
        };
    });

    const categoryTotals = categories[0].category.slice().map((categoryItem, idx) => {
        return {
            label: categoryItem.label,
            idx,
            total: dataset.reduce((total, serie) => {
                return (total += Number(serie.data[idx].value));
            }, 0)
        };
    });
    const sortedTotals = categoryTotals.sort((a, b) => b.total - a.total).slice(0, maxValues);
    const topValuesidx = sortedTotals.map(item => item.idx);
    const finalCategories = categories[0].category.filter((categoryItem, idx) =>
        topValuesidx.includes(idx)
    );
    const topValueDataset = dataset.map(serie => {
        return {
            seriesname: serie.seriesname,
            origSerieName: serie.origSerieName,
            data: serie.data.filter((item, idx) => topValuesidx.includes(idx))
        };
    });
    categories[0].category = finalCategories;
    const finalDataset = topValueDataset.filter(serie =>
        serie.data.some(item => item.value !== '0')
    );

    return {...dataSource, categories, dataset: finalDataset};
};

const hebrewToEnglishMonths = {
    ['ינו']: 'Jan',
    ['פבר']: 'Feb',
    ['מרץ']: 'Mar',
    ['אפר']: 'Apr',
    ['מאי']: 'May',
    ['יונ']: 'Jun',
    ['יול']: 'Jul',
    ['אוג']: 'Aug',
    ['ספט']: 'Sep',
    ['אוק']: 'Oct',
    ['נוב']: 'Nov',
    ['דצמ']: 'Dec'
};

const hebrewFullToEnglishMonths = {
    ['ינואר']: 'January',
    ['פברואר']: 'February',
    ['מרץ']: 'March',
    ['אפריל']: 'April',
    ['מאי']: 'May',
    ['יוני']: 'June',
    ['יולי']: 'July',
    ['אוגוסט']: 'August',
    ['ספטמבר']: 'September',
    ['אוקטובר']: 'October',
    ['נובמבר']: 'November',
    ['דצמבר']: 'December'
};

const monthIndexMap: {[key: string]: number} = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11
};

const fullMonthIndexMap: {[key: string]: number} = {
    January: 0,
    February: 1,
    March: 2,
    April: 3,
    May: 4,
    June: 5,
    July: 6,
    August: 7,
    September: 8,
    October: 9,
    November: 10,
    December: 11
};

const quartersMap: {[key: string]: {start: number; end: number}} = {
    ['1Q']: {start: 0, end: 2},
    ['2Q']: {start: 3, end: 5},
    ['3Q']: {start: 6, end: 8},
    ['4Q']: {start: 9, end: 11}
};

const dateRangeFormatIds: number[] = [24, 25, 34, 35, 36];

const getNumericYear = (year: string) => {
    return Number(year) < 50 ? '20' + year : '19' + year;
};

const getYearAndMonthByDateFormat = (format: number, value: string) => {
    const rtl = isRTL();
    if (format === 35) {
        const yearAndMonth = value.split('-');
        const [a, b] = yearAndMonth;
        const yy = isNaN(Number(a)) ? b : a;
        const month = yy === a ? b : a;
        const year = getNumericYear(yy);
        return {year, month: rtl ? hebrewToEnglishMonths[month] : month};
    } else {
        const yearAndMonth = value.split(',');
        const yearIndex = rtl ? 1 : 0;
        const monthIndex = rtl ? 0 : 1;
        const month = yearAndMonth[monthIndex].trim();
        return {
            year: yearAndMonth[yearIndex].trim(),
            month: rtl ? hebrewToEnglishMonths[month] : month
        };
    }
};

export const getDataplotOperator = (dateFormat, operators) => {
    if (Number(dateFormat) > 0 && dateRangeFormatIds.includes(Number(dateFormat))) {
        return operators.find(op => op.name.trim() === '-');
    } else {
        return operators.find(op => op.name.trim() === '=');
    }
};

const getStartAndEndDateValues = (dateType: string, start: Date, end: Date) => {
    const lang = getLang();
    const dateFormat = lang === '3' ? 'en-US' : 'en-GB';
    const options: Intl.DateTimeFormatOptions = {
        year: dateType === 'Date10' ? 'numeric' : '2-digit',
        month: '2-digit',
        day: '2-digit'
    };
    return {
        value: new Intl.DateTimeFormat(dateFormat, options).format(start),
        value2: new Intl.DateTimeFormat(dateFormat, options).format(end)
    };
};

const getStandardDate = (dateType: string, value: Date) => {
    const lang = getLang();
    const dateFormat = lang === '3' ? 'en-US' : 'en-GB';
    const options: Intl.DateTimeFormatOptions = {
        year: dateType === 'Date10' ? 'numeric' : '2-digit',
        month: '2-digit',
        day: '2-digit'
    };
    return {
        value: new Intl.DateTimeFormat(dateFormat, options).format(value),
        value2: ''
    };
};

const getStandardDate14 = (date: Date, time: string = null) => {
    let res: string;
    const lang = getLang();
    const dateFormat = lang === '3' ? 'en-US' : 'en-GB';
    const basicOptions: Intl.DateTimeFormatOptions = {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
    };
    if (time) {
        const options: Intl.DateTimeFormatOptions = {
            ...basicOptions,
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23'
        };
        const [hour, minute] = time.split(':');
        date.setHours(Number(hour));
        date.setMinutes(Number(minute));
        const dateTimeFormat = new Intl.DateTimeFormat(dateFormat, options).format(date);
        res = dateTimeFormat.replace(',', '');
    }
    const dateTimeFormat = new Intl.DateTimeFormat(dateFormat, basicOptions).format(date);
    const defaultTimeString = lang === '3' ? ' ??:?? ??' : ' ??:??';
    res = dateTimeFormat + defaultTimeString;
    return {
        value: res,
        value2: ''
    };
};

const getSerializedUngroupedDate14 = (dateString: string) => {
    const colon = ':';
    const timeStringLength = 8;
    const timeString = dateString.slice(-timeStringLength);
    const date = dateString.slice(0, -timeStringLength);
    const [time, period] = timeString.split(' ');
    const parsedHours = parseHours(time, period);
    const minutes = time.split(colon)[1];
    const time24Hrs = parsedHours + colon + minutes;
    return date + time24Hrs;
};

const getValueByFormat = (dateProps: DateProps, value: string) => {
    const format = Number(dateProps.dateFormat);
    const isDate14 = dateProps.type === 'Date14';
    let isHebrew = isRTL();
    let year,
        day,
        month,
        date,
        dateParts,
        dayIndex,
        monthIdx,
        yearIndex,
        monthName,
        splitter,
        isNumericYear;

    switch (format) {
        case 11: // DD/MM/YY
        case 12: // DD/MM/YYYY
        case 13: // MM/DD/YY
        case 14: // MM/DD/YYYY
        case 21: // DD MM YYYY
            isNumericYear = format === 12 || format === 14 || format === 21;
            const isEnUsFormat = format === 13 || format === 14;
            splitter = format === 21 ? ' ' : '/';
            dateParts = value.split(splitter);
            year = isNumericYear ? dateParts[2] : getNumericYear(dateParts[2]);
            dayIndex = isEnUsFormat ? 1 : 0;
            monthIdx = isEnUsFormat ? 0 : 1;
            day = Number(dateParts[dayIndex]);
            month = Number(dateParts[monthIdx]) - 1;
            date = new Date(year, month, day);
            return isDate14 ? getStandardDate14(date) : getStandardDate(dateProps.type, date);

        case 15: // MMM DD, YYYY
        case 16: // mmm DD, YYYY
            if (format === 16) {
                isHebrew = false;
            }
            dateParts = value.split(' ');
            yearIndex = isHebrew ? 0 : 2;
            year = dateParts[yearIndex];
            monthName = isHebrew ? hebrewToEnglishMonths[dateParts[2]] : dateParts[0];
            month = monthIndexMap[monthName];
            day = Number(dateParts[1].replace(',', ''));
            date = new Date(year, month, day);
            return isDate14 ? getStandardDate14(date) : getStandardDate(dateProps.type, date);

        case 17: // DD-MMM-YYYY
        case 18: // DD-mmm-YYYY
        case 22: // DD mmm YY
        case 23: // DD MMM YYYY
        case 26: // DD MMM YY
        case 28: // DD mmmm YY
        case 31: // DD MMMM YY
            if (format === 18 || format === 22 || format === 28) {
                isHebrew = false;
            }
            splitter = format > 20 ? ' ' : '-';
            isNumericYear = format === 17 || format === 18 || format == 23;
            dateParts = value.split(splitter);
            const yearValue = isHebrew ? dateParts[0] : dateParts[2];
            year = isNumericYear ? yearValue : Number(getNumericYear(yearValue));
            const isFullMonth = format === 28 || format === 31;
            monthName = !isHebrew
                ? dateParts[1]
                : isFullMonth
                ? hebrewFullToEnglishMonths[dateParts[1]]
                : hebrewToEnglishMonths[dateParts[1]];
            month = isFullMonth ? fullMonthIndexMap[monthName] : monthIndexMap[monthName];
            day = isHebrew ? Number(dateParts[2]) : Number(dateParts[0]);
            date = new Date(year, month, day);
            return isDate14 ? getStandardDate14(date) : getStandardDate(dateProps.type, date);

        case 19: // YYYY-MMM-DD
            dateParts = value.split('-');
            yearIndex = isHebrew ? 2 : 0;
            year = dateParts[yearIndex];
            monthName = isHebrew ? hebrewToEnglishMonths[dateParts[1]] : dateParts[1];
            month = monthIndexMap[monthName];
            day = isHebrew ? Number(dateParts[0]) : Number(dateParts[2]);
            date = new Date(year, month, day);
            return isDate14 ? getStandardDate14(date) : getStandardDate(dateProps.type, date);

        case 25: // YEAR YYYY
            year = Number(value);
            const startOfYear = new Date(year, 0, 1);
            const endOfYear = new Date(year, 11, 31);
            return getStartAndEndDateValues(dateProps.type, startOfYear, endOfYear);

        case 32: // DD-MM-YYYY hh:mm
        case 33: // DD-MM-YY hh:mm
            dateParts = value.split('-');
            const yearAndTime = dateParts[2].split(' ');
            year = format === 32 ? yearAndTime[0] : Number(getNumericYear(yearAndTime[0]));
            day = Number(dateParts[0]);
            month = Number(dateParts[1]) - 1;
            date = new Date(year, month, day);
            return isDate14
                ? getStandardDate14(date, yearAndTime[1])
                : getStandardDate(dateProps.type, date);

        case 34: // WEEKS YYYYWW
            year = Number(value.substring(0, 4));
            const week = Number(value.substring(4));
            date = new Date(year, 0, 1); // First day of the year
            date.setDate(date.getDate() + (week - 1) * 7);
            const dayOfWeek = date.getDay(); // Day of the week (0-6, Sunday-Saturday)
            const startOfWeek = new Date(date);
            startOfWeek.setDate(date.getDate() - dayOfWeek); // Set to Sunday
            const endOfWeek = new Date(date);
            endOfWeek.setDate(date.getDate() + (6 - dayOfWeek)); // Set to Saturday
            return getStartAndEndDateValues(dateProps.type, startOfWeek, endOfWeek);

        /* MONTHS */
        case 35: // YY-MMM
        case 24: // YYYY, MMM
            const dateValues = getYearAndMonthByDateFormat(format, value);
            year = dateValues.year;
            month = dateValues.month;
            const monthIndex = monthIndexMap[month];
            const startOfMonth = new Date(year, monthIndex, 1);
            const endOfMonth = new Date(year, monthIndex + 1, 0);
            return getStartAndEndDateValues(dateProps.type, startOfMonth, endOfMonth);

        case 36: // QUARTERS YYYY/QQ
            const yearAndQuarter = value.split('/');
            year = Number(yearAndQuarter[0]);
            const quarter = yearAndQuarter[1];
            const startOfQuarter = new Date(year, quartersMap[quarter].start, 1);
            const endOfQuarter = new Date(year, quartersMap[quarter].end + 1, 0);
            return getStartAndEndDateValues(dateProps.type, startOfQuarter, endOfQuarter);
        default:
            return {value: value, value2: ''};
    }
};

export const getSerializedDataplotValues = (dateProps, value) => {
    if (dateProps.isGrouped && Number(dateProps.dateFormat) > 0) {
        const values = getValueByFormat(dateProps, value);
        return {value: values.value, value2: values.value2};
    } else if (getLang() === '3' && dateProps.type === 'Date14') {
        const serializedDateString = getSerializedUngroupedDate14(value);
        return {value: serializedDateString, value2: ''};
    } else {
        return {value: value, value2: ''};
    }
};

export const getDataplotValue = (
    dimensionsValues: string[],
    inputMetaData: ReportInputMetaData
) => {
    if (dimensionsValues.length > 1 && inputMetaData.dataplotDimension === ChartDimensions.series) {
        return dimensionsValues[1];
    } else {
        return dimensionsValues[0];
    }
};
